import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { orgUnitsApi } from '../../../human-resources/organization/organizationApi';
import type { OrgUnitOption } from '../../../human-resources/organization/organizationTypes';
import { positionsApi } from '../../../human-resources/positions/positionsApi';
import type { PositionOption } from '../../../human-resources/positions/positionTypes';
import { surveysApi } from '../surveyApi';
import type { Survey } from '../surveyTypes';
import { sampleSurvey } from '../utils/sampleSurvey';
import { SurveyTargetPicker } from './SurveyTargetPicker';

/**
 * Create or edit one survey: its title, and the units and positions it covers.
 *
 * Divisions are units on Human Resources › Organization's chart and positions rows of its
 * position catalog — both read through the implied reads `surveys` takes on its create and
 * update keys. Both are OPTIONAL and MULTIPLE, and **leaving one empty means ALL**, which is
 * what each table says while it has no row. The number of respondents is not a field: it is counted by the
 * server, never typed.
 */
export function SurveyModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: Survey | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const toast = useToast();
  const [title, setTitle] = useState(editing?.title ?? '');
  // Empty is ALL, so a new survey opens on All for both.
  const [orgUnitIds, setOrgUnitIds] = useState<string[]>(editing?.orgUnits.map((t) => t.id) ?? []);
  const [positionIds, setPositionIds] = useState<string[]>(editing?.positions.map((t) => t.id) ?? []);

  const [units, setUnits] = useState<OrgUnitOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    Promise.all([
      orgUnitsApi.options(undefined, controller.signal),
      positionsApi.options(controller.signal),
    ])
      .then(([nextUnits, nextPositions]) => {
        if (controller.signal.aborted) return;

        setUnits(nextUnits);
        setPositions(nextPositions);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });

    return () => controller.abort();
  }, [open]);

  /*
    The feeds list ACTIVE rows only. On an edit covering a division or position deleted since,
    the recorded one is put back as an option so its chip still names it — keeping it is
    allowed; picking it afresh is what the server refuses.
  */
  const unitOptions = [
    ...units.map((u) => ({ value: u.id, label: u.name })),
    ...(editing?.orgUnits ?? [])
      .filter((t) => !units.some((u) => u.id === t.id))
      .map((t) => ({ value: t.id, label: t.name })),
  ];

  const positionOptions = [
    ...positions.map((p) => ({ value: p.id, label: p.title })),
    ...(editing?.positions ?? [])
      .filter((t) => !positions.some((p) => p.id === t.id))
      .map((t) => ({ value: t.id, label: t.name })),
  ];

  /*
    POSITIONS FOLLOW THE UNITS (product call, 13 Oct 2026): once a unit is named, the picker offers
    only the positions with a post in it or beneath it. No unit named is ALL, so nothing narrows.
    A failed lookup leaves the whole catalog on offer — the server still decides what a survey covers.
  */
  const unitKey = [...orgUnitIds].sort().join(',');
  const [offer, setOffer] = useState<{ key: string; ids: ReadonlySet<string> | null } | null>(null);

  useEffect(() => {
    if (!unitKey) return;

    const controller = new AbortController();

    surveysApi
      .positionIdsInUnits(unitKey.split(','), controller.signal)
      .then((ids) => {
        if (!controller.signal.aborted) setOffer({ key: unitKey, ids: new Set(ids) });
      })
      .catch(() => {
        if (!controller.signal.aborted) setOffer({ key: unitKey, ids: null });
      });

    return () => controller.abort();
  }, [unitKey]);

  const offerReady = !unitKey || offer?.key === unitKey;
  const offeredPositions = unitKey && offer?.key === unitKey ? (offer.ids ?? undefined) : undefined;

  const valid = title.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { title: title.trim(), orgUnitIds, positionIds };

    try {
      if (editing) await surveysApi.update(editing.id, request);
      else await surveysApi.create(request);

      toast.success(editing ? 'Survey updated.' : 'Survey created.');

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this survey.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'survey' });
    } finally {
      setSaving(false);
    }
  }

  async function generate() {
    const sample = sampleSurvey(units, positions);

    setTitle(sample.title);
    setOrgUnitIds(sample.orgUnitIds);

    // The sample's positions are held to the same rule the picker is: only those in its units.
    let positionIds = sample.positionIds;

    if (sample.orgUnitIds.length > 0) {
      try {
        const inUnits = new Set(await surveysApi.positionIdsInUnits(sample.orgUnitIds));
        positionIds = positionIds.filter((id) => inUnits.has(id));
      } catch {
        // Left as sampled — the lookup is a convenience, never a rule.
      }
    }

    setPositionIds(positionIds);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Names the survey on an edit, so a reader who opened the wrong row sees it before they type.
      title={editing ? `Edit survey — ${editing.title}` : 'New survey'}
      size="lg"
      id="survey-modal"
      autogenerate={{ onGenerate: () => void generate(), disabled: saving || loadingOptions }}
      confirm={{
        id: 'survey-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="survey-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            id="survey-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="survey-title"
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={title.trim() ? error?.fieldError('title') : 'Enter a title.'}
          disabled={saving}
        />

        {/*
          SEARCH, THEN A TABLE — the Share dialog's layout (product call, 13 Oct 2026). Each is a
          full-width block of its own: a picker that adds a row, over the rows added.
        */}
        <SurveyTargetPicker
          id="survey-org-unit-ids"
          label="Units/Organization"
          description="Units on Human Resources › Organization's chart. Leave it empty to cover every unit."
          placeholder="Search units to add…"
          column="Units/Organization"
          allLabel="All units/organizations"
          value={orgUnitIds}
          onChange={setOrgUnitIds}
          options={unitOptions}
          loading={loadingOptions}
          disabled={saving}
          error={error?.fieldError('orgUnitIds')}
        />

        <SurveyTargetPicker
          id="survey-position-ids"
          label="Position"
          description="Positions from Human Resources › Organization › Settings › Positions — only those in the units chosen above, once any is. Leave it empty to cover every position."
          placeholder="Search positions to add…"
          column="Position"
          allLabel="All positions"
          value={positionIds}
          onChange={setPositionIds}
          options={positionOptions}
          offered={offeredPositions}
          loading={loadingOptions || !offerReady}
          disabled={saving}
          error={error?.fieldError('positionIds')}
        />
      </div>
    </Modal>
  );
}
